/* eslint-disable import/no-cycle */
import moment from 'moment-timezone';
import templateSettings from 'lodash/templateSettings';
import template from 'lodash/template';
import * as _ from '../lodash';
import Translation from './localizationUtility';
import {
  parseTime as parseTime1,
  isValidDate,
  isValidISOFormat,
} from './timeUtility';
import { parseStatus } from '../page/reservationPage/parser';

export const prefixZero = (string, size) => {
  let s = `${string}`;
  while (s.length < size) s = `0${s}`;
  return s;
};

export const capitalize = (value) => _.capitalize(value);

export const parseInt = (value) => (+value ? +value : 0);

export const findProp = (props, names, options = false) => {
  if (!props) return false;

  const keys = Object.keys(props);
  for (let x = 0; x < keys.length; x++) {
    for (let y = 0; y < names.length; y++) {
      if (keys[x].toLowerCase().indexOf(names[y]) === 0 && keys[x].length === names[y].length) {
        if (!options || !options.type) {
          return props[keys[x]];
        }

        const { type } = options;
        switch (type) {
          case 'date':
            if (options.format) {
              if (isValidDate(props[keys[x]], options.format)) {
                return props[keys[x]];
              }
            }
            if (isValidISOFormat(props[keys[x]])) {
              return props[keys[x]];
            }
            break;
          case 'time':
            if (parseTime1(props[keys[x]]).isValid()) {
              return props[keys[x]];
            }
            break;
          default:
            break;
        }
      }
    }
  }

  return false;
};

const interpolateMessageToTemplate = (str, value) => {
  let message;

  templateSettings.interpolate = /{{([\s\S]+?)}}/g;

  const templateFunc = template(str);

  try {
    message = templateFunc({ spaces_count: value });
  } catch {
    message = str;
  }

  return message;
};

export const formatAvailabilityMessage = (available, total, translation = new Translation({})) => {
  let message;

  const templateTranslation = translation.key('textForShowRemainingPlaces') || '{{spaces_count}}';
  const space = translation.key('oneSpace') || 'space';
  const spaces = translation.key('multipleSpaces') || 'spaces';
  const soldOut = translation.key('soldOut') || 'Sold out';

  if (available <= 0) {
    message = soldOut;
  } else if (total > available) {
    message = `Only ${available} ${space}`;
  } else if (available === 1) {
    message = interpolateMessageToTemplate(templateTranslation, `${available} ${space}`);
  } else {
    message = interpolateMessageToTemplate(templateTranslation, `${available} ${spaces}`);
  }
  return message;
};

export const parseTime = (message) => {
  let output = message;
  const utcDates = output.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/g);
  if (utcDates) {
    utcDates.forEach((date) => {
      output = output.replace(date, moment.tz(date, window.bta.timeZone).format('LLL (z)'));
    });
  }
  return output;
};

export const findUnits = (item) => {
  const unitsNames = ['units', 'booking-party', 'booking-units'];
  const units = findProp(item.properties, unitsNames);
  if (units) {
    return ~~units;
  }

  return item.quantity;
};

export const flatten = (input, depth = 1, stack = []) => {
  for (const item of input) {
    if (item instanceof Array) {
      flatten(item, depth - 1, stack);
    } else {
      stack.push(item);
    }
  }

  return stack;
};

export const parseAvailableDates = (timeslots, minDate, maxDate, timeZone) => {
  const now = moment.tz(timeZone);
  const today = now.clone().startOf('day');
  const minDay = now.clone().add(minDate, 'seconds').startOf('day');
  const maxDay = now.clone().add(maxDate, 'days').startOf('day');
  return timeslots.reduce((arr, timeslot) => {
    const theDate = moment.tz(timeslot.date, timeZone);
    let isValid = false;
    if (timeslot.available_slot_count > 0) {
      if (theDate.isSameOrAfter(today)) {
        if (minDate) {
          if (theDate.isSameOrAfter(minDay)) {
            if (maxDate) {
              if (maxDay.isSameOrAfter(theDate)) {
                isValid = true;
              }
            } else {
              isValid = true;
            }
          }
        } else if (maxDate) {
          if (maxDay.isSameOrAfter(theDate)) {
            isValid = true;
          }
        } else {
          isValid = true;
        }
      }
    }

    if (isValid && _.find(timeslot.timeslots, (x) => moment.tz(x.start, timeZone).isSameOrAfter(now))) {
      arr.push(timeslot.date);
    }
    return arr;
  }, []);
};

const selectOptionOpacity = (state) => {
  if (state.isDisabled) return '0.5';
  return state.isSelected ? '0.8' : '1';
};

const selectOptionBackgroundColor = (state, styles) => {
  const { isSelected, isFocused } = state;
  const { hover } = styles;
  let { backgroundColor } = styles;
  if (hover && (isSelected || isFocused)) backgroundColor = hover.backgroundColor;
  return backgroundColor;
};

export const customSelectStyles = (styles) => {
  if (!styles) return {};

  return {
    control: (provided) => ({
      ...provided,
      ...{ ...styles, boxShadow: 'unset', borderColor: `${styles.color} !important` },
    }),
    menu: (provided) => ({
      ...provided,
      ...styles,
      ...{
        zIndex: 2
      }
    }),
    option: (provided, state) => ({
      ...provided,
      ...{
        color: styles.color,
        opacity: selectOptionOpacity(state),
        backgroundColor: selectOptionBackgroundColor(state, styles),
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      ...styles,
    }),
    placeholder: (provided) => ({
      ...provided,
      ...{ ...styles },
    }),
  };
};

export const getStyleFromWidth = (width) => {
  let padding = '5px';
  let fontSize = '16px';

  if (width >= 220 && width < 250) {
    padding = '4px';
  } else if (width < 220) {
    padding = '3px';
    if (width < 165) fontSize = '12px';
  }

  return { padding, fontSize };
};

export const formattedPrice = (value) => {
  const formatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return formatter.format(value);
};

export const getScheduleConflictMessages = (reservation) => {
  let sm = ''; let
    fm = '';
  const item = reservation.items.find((x) => x.status !== 'ok');

  if (item) {
    sm = capitalize(item.status).replace('_', ' ');
    fm = capitalize(parseStatus(item.status));
  }

  return { fullMessage: fm, shortMessage: sm };
};
